import { useCallback, useEffect, useRef, useState } from 'react';
import { api } from '../lib/api';

type MyActiveSession = {
  sessionId: number;
  projectId: number;
  projectName: string;
};

export type SessionGuardianPrompt = {
  sessionId: number;
  projectId: number;
  projectName: string;
  awayFromIso: string;
};

// If the tab is hidden for longer than this while a session runs, prompt the
// user to retroactively close it (they likely walked away).
const AWAY_THRESHOLD_MS = 25 * 60 * 1000;

// While the app is visible and my session runs, persist a last-active
// timestamp so a cold reopen (PWA killed / tab discarded — visibilitychange
// never fires) can detect the away gap too.
const HEARTBEAT_MS = 45 * 1000;
const LAST_ACTIVE_KEY = 'whoworks:sessionGuardian:lastActiveAt';

type StoredHeartbeat = { sessionId: number; ts: number };

const readHeartbeat = (): StoredHeartbeat | null => {
  try {
    const raw = localStorage.getItem(LAST_ACTIVE_KEY);
    if (!raw) {
      return null;
    }
    const parsed = JSON.parse(raw) as Partial<StoredHeartbeat>;
    if (typeof parsed.sessionId !== 'number' || typeof parsed.ts !== 'number') {
      return null;
    }
    return { sessionId: parsed.sessionId, ts: parsed.ts };
  } catch {
    return null;
  }
};

const writeHeartbeat = (sessionId: number): void => {
  try {
    localStorage.setItem(LAST_ACTIVE_KEY, JSON.stringify({ sessionId, ts: Date.now() }));
  } catch {
    /* private mode / quota — guardian falls back to in-memory tracking */
  }
};

const clearHeartbeat = (): void => {
  try {
    localStorage.removeItem(LAST_ACTIVE_KEY);
  } catch {
    /* ignore */
  }
};

export const useSessionGuardian = (
  myActiveSession: MyActiveSession | null,
): { prompt: SessionGuardianPrompt | null; clearPrompt: () => void } => {
  const [prompt, setPrompt] = useState<SessionGuardianPrompt | null>(null);
  const hiddenAtRef = useRef<number | null>(null);
  // The cold-open check must run once per app load — the effect itself re-runs
  // every time the polled projects query produces a new myActiveSession object.
  const coldCheckDoneRef = useRef(false);
  // True once a session was observed in this app load — lets us clear the
  // persisted heartbeat when the session ends, without wiping it during the
  // initial loading phase (myActiveSession is null before the query resolves).
  const hadSessionRef = useRef(false);

  const clearPrompt = useCallback(() => {
    setPrompt(null);
  }, []);

  useEffect(() => {
    if (!myActiveSession) {
      hiddenAtRef.current = null;
      if (hadSessionRef.current) {
        // Session ended in this app load — the persisted heartbeat is no
        // longer evidence of anything; drop it.
        hadSessionRef.current = false;
        clearHeartbeat();
      }
      return;
    }

    const { sessionId, projectId, projectName } = myActiveSession;
    hadSessionRef.current = true;

    // Cold reopen: the PWA was killed or the tab discarded while my session
    // kept running — visibilitychange never fired, so compare against the
    // persisted heartbeat instead. Only trusted when it belongs to the same
    // session that is still running now.
    if (!coldCheckDoneRef.current) {
      coldCheckDoneRef.current = true;
      const stored = readHeartbeat();
      if (
        stored &&
        stored.sessionId === sessionId &&
        Date.now() - stored.ts > AWAY_THRESHOLD_MS
      ) {
        setPrompt({
          sessionId,
          projectId,
          projectName,
          awayFromIso: new Date(stored.ts).toISOString(),
        });

        // Fire-and-forget: record that the session was auto-paused due to inactivity.
        void api.eventsAutoPause('away').catch(() => {
          /* best-effort telemetry */
        });
      }
    }

    // Heartbeat: only while visible, so the stored timestamp marks the moment
    // the user was last actually present. Writing immediately also prevents a
    // re-prompt right after the cold check above.
    const beat = () => {
      if (document.visibilityState === 'visible') {
        writeHeartbeat(sessionId);
      }
    };
    beat();
    const heartbeatId = window.setInterval(beat, HEARTBEAT_MS);

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'hidden') {
        hiddenAtRef.current = Date.now();
        return;
      }

      const hiddenAt = hiddenAtRef.current;
      hiddenAtRef.current = null;
      if (hiddenAt === null) {
        return;
      }

      const awayMs = Date.now() - hiddenAt;
      if (awayMs <= AWAY_THRESHOLD_MS) {
        return;
      }

      setPrompt({
        sessionId,
        projectId,
        projectName,
        awayFromIso: new Date(hiddenAt).toISOString(),
      });

      // Fire-and-forget: record that the session was auto-paused due to inactivity.
      void api.eventsAutoPause('away').catch(() => {
        /* best-effort telemetry */
      });
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.clearInterval(heartbeatId);
      hiddenAtRef.current = null;
    };
  }, [myActiveSession]);

  return { prompt, clearPrompt };
};
